import { useEffect } from 'react';

import BpkButton, {
  BUTTON_TYPES,
} from '@skyscanner-internal/backpack-web/bpk-component-button';
import BpkCard from '@skyscanner-internal/backpack-web/bpk-component-card';
import BpkDivider from '@skyscanner-internal/backpack-web/bpk-component-divider';
import BpkSmallInformationCircleIcon from '@skyscanner-internal/backpack-web/bpk-component-icon/sm/information-circle';
import {
  BpkBox,
  BpkSpacing,
  BpkStack,
} from '@skyscanner-internal/backpack-web/bpk-component-layout';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';
import BpkTooltip from '@skyscanner-internal/backpack-web/bpk-component-tooltip';
import { useSelector } from 'react-redux';
import { logOperationalEvent } from 'saddlebag-logger';

import getFeatureDecision from '../../../../config';
import { COMBO_RECOMMENDATION_TYPE } from '../../../../config/constants';
import {
  RECOMMENDATIONS_CARD,
  COMPONENT_CAPYBARA_RECOMMENDATIONS,
} from '../../helpers/constants/analyticsConstants';
import { useTranslation } from '../../helpers/hooks/useTranslation';
import useComboRecommendationTracking from '../../middleware/trackBehaviour/useComboRecommendationTracking';
import {
  trackComboClick,
  trackComboImpression,
} from '../../middleware/trackEvent';
import {
  getSelectedOutboundDate,
  getSelectedInboundDate,
  getStay,
} from '../../selectors';

import CarHireSection from './CarHireSection';
import FlightSection from './FlightSection';
import HotelSection from './HotelSection';

import type { ComboRecommendation } from './types';

import './RecommendationCard.scss';

const removeEmpty = (
  obj: Record<string, string | undefined>,
): Record<string, string> =>
  Object.fromEntries(
    Object.entries(obj).filter(([, v]) => v != null),
  ) as Record<string, string>;

const formatDate = (date: Date | null): string | undefined => {
  if (!date) {
    return undefined;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

type RecommendationCardProps = {
  recommendation: ComboRecommendation;
};

const RecommendationCard = ({ recommendation }: RecommendationCardProps) => {
  const translate = useTranslation();
  const { tWithElements } = translate;
  const { carHire, dateRange, destination, flight, hotel } = recommendation;

  const isCheapest =
    getFeatureDecision(COMBO_RECOMMENDATION_TYPE, 'cheapest') === 'cheapest';
  const disclaimerKey = isCheapest
    ? 'ktxt_combo_rec_price_disclaimer_cheap'
    : 'ktxt_combo_rec_price_disclaimer_best';

  const pricesDisclaimerUrl = translate(
    'ktxt_combo_rec_price_disclaimer_more_url',
  );
  const outboundDate = useSelector(getSelectedOutboundDate);
  const inboundDate = useSelector(getSelectedInboundDate);
  const stay = useSelector(getStay);

  // Determine which verticals are present
  const verticals: string[] = [];
  if (flight) verticals.push('flight');
  if (hotel) verticals.push('hotel');
  if (carHire) verticals.push('car');

  const numericProperties: Record<string, number> = {
    flight_price: flight.priceAmount ?? -1,
    hotel_price: hotel.priceAmount ?? -1,
    total_price: recommendation.totalPriceAmount ?? -1,
    ...(carHire && { car_price: carHire.priceAmount ?? -1 }),
  };

  const { ref, trackClick } = useComboRecommendationTracking({
    componentName: 'combo_recommendation',
    stringProperties: removeEmpty({
      verticals: verticals.join(','),
      recommendation_type: recommendation.recommendationType,
      origin_entity_id: recommendation.originEntityId,
      destination_entity_id: recommendation.destinationEntityId,
      carrier_id: recommendation.carrierId,
      hotel_id: recommendation.hotelId,
      currency: recommendation.currency || undefined,
      outbound_date: formatDate(outboundDate),
      inbound_date: formatDate(inboundDate),
      adults: stay.adults == null ? undefined : String(stay.adults),
      children_count: String(stay.childrenAges.length),
    }),
    numericProperties,
  });

  useEffect(() => {
    trackComboImpression();
  }, []);

  return (
    <BpkBox ref={ref} data-testid="recommendation-card">
      <BpkCard className="recommendation-card" padded={false} atomic={false}>
        <BpkStack
          direction="column"
          gap={BpkSpacing.Base}
          paddingBlock={BpkSpacing.MD}
          paddingInline={BpkSpacing.Base}
        >
          <BpkStack direction="column" gap={BpkSpacing.SM}>
            <BpkStack
              direction="row"
              justify="space-between"
              align="flex-start"
            >
              <BpkText textStyle={TEXT_STYLES.label1} tagName="span">
                {destination}
              </BpkText>
              <BpkText textStyle={TEXT_STYLES.label1} tagName="span">
                {dateRange}
              </BpkText>
            </BpkStack>
            <BpkDivider />
          </BpkStack>

          <BpkStack
            direction={{ base: 'column', desktop: 'row' }}
            gap={BpkSpacing.LG}
          >
            <FlightSection
              flight={flight}
              onLinkClick={() => {
                logOperationalEvent({
                  component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
                  eventName: 'clicked',
                  vertical: 'flight',
                });
                trackComboClick(RECOMMENDATIONS_CARD.FLIGHT);
                trackClick({
                  clicked_element: 'flight_link',
                  redirect_url: flight.onClickUrl,
                  vertical: 'flight',
                });
              }}
            />
            <HotelSection
              hotel={hotel}
              onLinkClick={() => {
                logOperationalEvent({
                  component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
                  eventName: 'clicked',
                  vertical: 'hotel',
                });
                trackComboClick(RECOMMENDATIONS_CARD.HOTEL);
                trackClick({
                  clicked_element: 'hotel_link',
                  redirect_url: hotel.onClickUrl,
                  vertical: 'hotel',
                });
              }}
            />
            {carHire && (
              <CarHireSection
                carHire={carHire}
                onLinkClick={() => {
                  logOperationalEvent({
                    component: COMPONENT_CAPYBARA_RECOMMENDATIONS,
                    eventName: 'clicked',
                    vertical: 'car',
                  });
                  trackComboClick(RECOMMENDATIONS_CARD.CAR);
                  trackClick({
                    clicked_element: 'car_link',
                    redirect_url: carHire.onClickUrl,
                    vertical: 'car_hire',
                  });
                }}
              />
            )}
          </BpkStack>

          <BpkStack direction="column" align="center" gap={BpkSpacing.SM}>
            <BpkDivider />
            <BpkStack direction="row" align="center" gap={BpkSpacing.SM}>
              <BpkText textStyle={TEXT_STYLES.bodyDefault} tagName="span">
                {tWithElements('ktxt_combo_rec_our_estimate', {
                  price: (
                    <BpkText textStyle={TEXT_STYLES.label1} tagName="span">
                      {recommendation.totalPriceFormatted}
                    </BpkText>
                  ),
                  day: String(recommendation.tripDays),
                })}
              </BpkText>
              <BpkTooltip
                id="combo-rec-price-info"
                ariaLabel={translate('ktxt_combo_rec_price_disclaimer_more')}
                target={
                  <BpkButton
                    type={BUTTON_TYPES.link}
                    iconOnly
                    className="recommendation-card__info-button"
                    aria-label={translate(
                      'ktxt_combo_rec_price_disclaimer_more',
                    )}
                  >
                    <BpkSmallInformationCircleIcon aria-hidden="true" />
                  </BpkButton>
                }
                placement="bottom"
              >
                <BpkText textStyle={TEXT_STYLES.bodyDefault}>
                  {translate(disclaimerKey)}
                  <BpkLink
                    href={pricesDisclaimerUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {translate('ktxt_combo_rec_price_disclaimer_more')}
                  </BpkLink>
                </BpkText>
              </BpkTooltip>
            </BpkStack>
          </BpkStack>
        </BpkStack>
      </BpkCard>
    </BpkBox>
  );
};

export default RecommendationCard;
